// Device keys and space secrets (docs/adr/0004-identite-et-appareils.md).
//
// Each device owns two NON-extractable key pairs, generated here and kept in IndexedDB:
// Ed25519 to sign its journal envelopes, X25519 to receive secrets sealed for it. Only the
// public halves ever leave the device. A space ("me": all my devices) is a 32-byte secret per
// key epoch; it derives the content key, the rendezvous room and the room authentication key.

import { b64url, fromB64url, randomBytes, sha256Hex, toHex } from '../core/ids';
import { deriveAuthKey } from '../core/auth';

const enc = new TextEncoder();
const dec = new TextDecoder();

/**
 * What other devices know about a device. In a conversation it also carries its owner's
 * attestation (contacts/identity.ts): the person's identity key and its signature over the device.
 */
export type DeviceRecord = { id: string; name: string; signPub: string; dhPub: string; person?: string; personSig?: string };

/** This device, with its private keys (CryptoKey handles that cannot be exported). */
export type LocalDevice = DeviceRecord & { sign: CryptoKey; dh: CryptoKey };

/**
 * Secrets of a space, by key epoch. The space id never changes: it comes from epoch 0.
 * Two devices may rotate the key at the same time (same epoch, different secrets): the secret of
 * the smallest author id is used to write (`secrets`), the others are kept to read (`alts`).
 */
export type SpaceSecrets = {
  spaceId: string;
  epoch: number;
  secrets: Record<number, string>;
  alts?: Record<number, string[]>;
  rekeyAuthors?: Record<number, string>;
  /** Devices known to hold each epoch's secret (sealed for them, or given to them when added). */
  holders?: Record<number, string[]>;
};

export async function createDevice(name: string): Promise<LocalDevice> {
  const sign = (await crypto.subtle.generateKey({ name: 'Ed25519' }, false, ['sign', 'verify'])) as CryptoKeyPair;
  const dh = (await crypto.subtle.generateKey({ name: 'X25519' }, false, ['deriveBits'])) as CryptoKeyPair;
  const signPub = new Uint8Array(await crypto.subtle.exportKey('raw', sign.publicKey));
  const dhPub = new Uint8Array(await crypto.subtle.exportKey('raw', dh.publicKey));
  return { id: await deviceId(signPub), name, signPub: b64url(signPub), dhPub: b64url(dhPub), sign: sign.privateKey, dh: dh.privateKey };
}

export async function deviceId(signPub: Uint8Array): Promise<string> {
  return toHex(new Uint8Array(await crypto.subtle.digest('SHA-256', signPub as Uint8Array<ArrayBuffer>))).slice(0, 32);
}

export function publicRecord(d: DeviceRecord): DeviceRecord {
  return { id: d.id, name: d.name, signPub: d.signPub, dhPub: d.dhPub };
}

export async function sign(device: LocalDevice, data: Uint8Array): Promise<string> {
  return b64url(new Uint8Array(await crypto.subtle.sign({ name: 'Ed25519' }, device.sign, data as Uint8Array<ArrayBuffer>)));
}

const verifyKeys = new Map<string, Promise<CryptoKey>>();

export async function verify(signPub: string, sig: string, data: Uint8Array): Promise<boolean> {
  let key = verifyKeys.get(signPub);
  if (!key) {
    key = crypto.subtle.importKey('raw', fromB64url(signPub), { name: 'Ed25519' }, false, ['verify']);
    verifyKeys.set(signPub, key);
  }
  try {
    return await crypto.subtle.verify({ name: 'Ed25519' }, await key, fromB64url(sig), data as Uint8Array<ArrayBuffer>);
  } catch {
    return false;
  }
}

// ---- spaces ----

export async function newSpace(): Promise<SpaceSecrets> {
  const secret = b64url(randomBytes(32));
  return { spaceId: await spaceIdFor(secret), epoch: 0, secrets: { 0: secret } };
}

export async function spaceIdFor(genesisSecret: string): Promise<string> {
  return (await sha256Hex(`space-id-v1:${genesisSecret}`)).slice(0, 32);
}

export function currentSecret(s: SpaceSecrets): string {
  return s.secrets[s.epoch];
}

/** Rendezvous room of the space for one epoch (a revoked device loses it at the next epoch). */
export function spaceRoom(secret: string): Promise<string> {
  return sha256Hex(`space-room-v1:${secret}`);
}

/** Room authentication: same DTLS-bound proof as share links, keyed by the space secret. */
export function spaceAuthKey(secret: string): Promise<CryptoKey> {
  return deriveAuthKey(`space-auth-v1:${secret}`);
}

const contentKeys = new Map<string, Promise<CryptoKey>>();

/** AES-256-GCM key that encrypts the envelopes of one epoch. */
export function contentKey(secret: string): Promise<CryptoKey> {
  let key = contentKeys.get(secret);
  if (!key) {
    key = (async () => {
      const base = await crypto.subtle.importKey('raw', fromB64url(secret), 'HKDF', false, ['deriveKey']);
      return crypto.subtle.deriveKey(
        { name: 'HKDF', hash: 'SHA-256', salt: enc.encode('space-v1'), info: enc.encode('content') },
        base,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt'],
      );
    })();
    contentKeys.set(secret, key);
  }
  return key;
}

// ---- sealing a secret for one device (X25519 + HKDF + AES-GCM) ----

export type Sealed = { from: string; nonce: string; ct: string };

async function pairKey(local: LocalDevice, otherDhPub: string, usage: KeyUsage): Promise<CryptoKey> {
  const pub = await crypto.subtle.importKey('raw', fromB64url(otherDhPub), { name: 'X25519' }, false, []);
  const shared = await crypto.subtle.deriveBits({ name: 'X25519', public: pub }, local.dh, 256);
  const base = await crypto.subtle.importKey('raw', shared, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: enc.encode('space-v1'), info: enc.encode('seal') },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    [usage],
  );
}

export async function sealFor(local: LocalDevice, recipientDhPub: string, plaintext: string): Promise<Sealed> {
  const nonce = randomBytes(12);
  const key = await pairKey(local, recipientDhPub, 'encrypt');
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce }, key, enc.encode(plaintext)));
  return { from: local.id, nonce: b64url(nonce), ct: b64url(ct) };
}

export async function openSealed(local: LocalDevice, senderDhPub: string, sealed: Sealed): Promise<string> {
  const key = await pairKey(local, senderDhPub, 'decrypt');
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: fromB64url(sealed.nonce) }, key, fromB64url(sealed.ct));
  return dec.decode(pt);
}
